import React from 'react';
import { BookPreviewData, BookPreviewChapter, BookPreviewSection } from './BookPreview';

interface BookPreviewHeaderProps {
  bookData: BookPreviewData;
  currentChapter?: BookPreviewChapter;
  currentSection?: BookPreviewSection;
  progress: number;
  onClose?: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
}

export const BookPreviewHeader: React.FC<BookPreviewHeaderProps> = ({
  bookData,
  currentChapter,
  currentSection,
  progress,
  onClose,
  onToggleFullscreen,
  isFullscreen = false
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'text-green-600 bg-green-100';
      case 'draft':
        return 'text-yellow-600 bg-yellow-100';
      case 'archived':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'published':
        return 'Đã xuất bản';
      case 'draft':
        return 'Bản nháp';
      case 'archived':
        return 'Đã lưu trữ';
      default:
        return 'Không xác định';
    }
  };

  return (
    <div className="book-preview-header bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Book Info */}
          <div className="flex items-center space-x-4 flex-1 min-w-0">
            {/* Cover Image */}
            {bookData.cover_image && (
              <div className="flex-shrink-0">
                <img
                  src={bookData.cover_image}
                  alt={bookData.title}
                  className="w-12 h-16 object-cover rounded border border-gray-200 dark:border-gray-600"
                />
              </div>
            )}

            {/* Book Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {bookData.title}
                </h1>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(bookData.status)}`}>
                  {getStatusText(bookData.status)}
                </span>
              </div>
              
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                {bookData.author && (
                  <span>Tác giả: {bookData.author}</span>
                )}
                {bookData.category && (
                  <span>Thể loại: {bookData.category}</span>
                )}
                {bookData.contents.length > 0 && (
                  <span>{bookData.contents.length} chương</span>
                )}
              </div>

              {/* Current Position */}
              {currentChapter && currentSection && (
                <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">{currentChapter.title}</span>
                  <span className="mx-2">•</span>
                  <span>{currentSection.title}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 ml-4">
            {/* Progress */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {Math.round(progress)}%
              </span>
              <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Fullscreen Toggle */}
            {onToggleFullscreen && (
              <button
                onClick={onToggleFullscreen}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title={isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}
              >
                {isFullscreen ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>
            )}

            {/* Close Button */}
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Đóng preview"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-3">
          <div className="w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
